import type { TFunction } from "i18next";
import { differenceInCalendarDays } from "date-fns";

import type {
  ISeatStatus,
  ISubscriptionCurrentPlan,
  ISubscriptionTokenUsage,
} from "@/types/(company)/subscription.interface";
import { formatDate } from "@/utils/helper";
import { usageStatus, type UsageStatus } from "@/utils/subscription.util";

import type { IPlanIdentity, IPlanResource } from "./types";

const SEVERITY: Record<UsageStatus, number> = {
  healthy: 0,
  warning: 1,
  critical: 2,
};

/** The status the collapsed header ring should surface. */
export function worstStatus(resources: IPlanResource[]): UsageStatus {
  return resources.reduce<UsageStatus>(
    (worst, r) => (SEVERITY[r.status] > SEVERITY[worst] ? r.status : worst),
    "healthy",
  );
}

function ratio(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(Math.max((part / whole) * 100, 0), 100);
}

/** Ring color follows the worst resource, so the arc follows it too. */
export function ringResource(resources: IPlanResource[]): IPlanResource | undefined {
  const status = worstStatus(resources);
  return resources.find((r) => r.status === status) ?? resources[0];
}

const nf = (value: number) => value.toLocaleString();

/** Counted nouns go through i18next plurals — Arabic has six forms, not two. */
const seats = (t: TFunction, count: number) =>
  t("company:subscription.seat_count", { count, formatted: nf(count) });
const days = (t: TFunction, count: number) =>
  t("company:subscription.day_count", { count, formatted: nf(count) });

/** "24 seats free" / "3 over capacity" / a scheduled seat change wins over both. */
function seatMeta(
  seatStatus: ISeatStatus,
  used: number,
  purchased: number,
  t: TFunction,
): string {
  if (seatStatus.pendingChange) {
    return t("company:subscription.seats_change_on", {
      seats: seats(t, seatStatus.pendingChange.targetSeats),
      date: formatDate(seatStatus.pendingChange.effectiveAt),
    });
  }
  if (used > purchased)
    return t("company:subscription.seats_over_capacity", {
      seats: seats(t, used - purchased),
    });
  return t("company:subscription.seats_free", {
    seats: seats(t, purchased - used),
  });
}

/** "Resets today" / "Resets in 12 days" — undefined when the period is unknown. */
function tokenMeta(
  periodEndDate: string | undefined,
  t: TFunction,
): string | undefined {
  if (!periodEndDate) return undefined;
  const end = new Date(periodEndDate);
  if (Number.isNaN(end.getTime())) return undefined;

  const dayCount = Math.max(differenceInCalendarDays(end, new Date()), 0);
  if (dayCount === 0) return t("company:subscription.resets_today");
  return t("company:subscription.resets_in", { days: days(t, dayCount) });
}

/**
 * Formats the raw subscription payloads into the rows the hub renders.
 *
 * Both rows are metered on the **percent used** axis and phrased like the
 * management cards they link to ("Seat Usage" / "Token Usage This Period"),
 * so the bar a user sees here is the bar they land on.
 *
 * Seats are omitted when the plan reports no purchased seats (nothing to meter).
 */
export function buildPlanResources({
  seats,
  tokens,
  t,
}: {
  seats?: ISeatStatus;
  tokens?: ISubscriptionTokenUsage;
  t: TFunction;
}): IPlanResource[] {
  const resources: IPlanResource[] = [];

  const purchasedSeats = seats?.purchasedSeats ?? 0;
  if (seats && purchasedSeats > 0) {
    const used = seats.activeEmployees;
    const percentUsed = Math.round(ratio(used, purchasedSeats));

    resources.push({
      key: "seats",
      label: t("company:subscription.seats"),
      value: t("company:subscription.n_of_m_in_use", { used: nf(used), total: nf(purchasedSeats) }),
      meta: seatMeta(seats, used, purchasedSeats, t),
      percentage: percentUsed,
      percentLabel: t("company:subscription.percent_used", { percent: percentUsed }),
      status: usageStatus(percentUsed),
    });
  }

  if (tokens) {
    // Same field the token management card reads, so the two never disagree.
    const percentUsed = tokens.usagePercent ?? 0;
    const allocated = tokens.totalAllocated;
    const remaining = Math.max(
      tokens.totalRemaining ?? allocated - tokens.totalConsumed,
      0,
    );

    resources.push({
      key: "tokens",
      label: t("company:subscription.ai_tokens"),
      value: t("company:subscription.n_of_m_remaining", { remaining: nf(remaining), total: nf(allocated) }),
      meta: tokenMeta(tokens.periodEndDate, t),
      percentage: percentUsed,
      percentLabel: t("company:subscription.percent_used", { percent: percentUsed }),
      status: usageStatus(percentUsed),
    });
  }

  return resources;
}

/** Plan identity for the hero band. */
export function buildPlanIdentity(
  plan: ISubscriptionCurrentPlan | undefined,
  t: TFunction,
): IPlanIdentity {
  if (!plan) return { isTrialing: false };

  const renewalNote = plan.isTrialing
    ? plan.trialEndsAt &&
      t("company:subscription.trial_ends", { date: formatDate(plan.trialEndsAt) })
    : plan.nextBillingDate &&
      t("company:subscription.renews_on", { date: formatDate(plan.nextBillingDate) });

  return {
    name: plan.name,
    isTrialing: plan.isTrialing,
    renewalNote: renewalNote || undefined,
  };
}
